import React from 'react';
import useModal from '@/hooks/useModal';
import { Button, Typography } from '@/components/common';
import { ModalHeader } from '../../modals/ModalHeader';
import { useNotification } from '@/hooks';
import { useRouter } from 'next/router';
import { actionRequiredService, casesService, GET_OPT_ACCEPTED } from '@/api';
import { formatErrorMessage } from '@/lib';
import Link from 'next/link';
import { InputText } from '@/components/form';
import { useForm } from 'react-hook-form';
import { OptAcceptPayload } from '@/interfaces';
import { format } from 'date-fns';
import Icons from '@/const/icons';
import { useQuery } from '@tanstack/react-query';

interface ModalProps {
	applicationId: number;
	actionReqId: number;
	url: string;
	caseName: string;
	isOpen: boolean;
	isAccepted?: boolean;
	setIsModalOpen: (value: boolean) => void;
	refetch?: () => void;
}

export const ModalAcceptOptOut: React.FC<ModalProps> = ({
	applicationId,
	actionReqId,
	// caseName,
	url,
	isOpen,
	isAccepted,
	setIsModalOpen,
	refetch,
}) => {
	const router = useRouter();
	const { id: caseId } = router.query;
	const { pathname, query } = router;
	const { Modal, show, hide } = useModal();
	const { toastError, toastSuccess } = useNotification();
	const { register, getValues, setValue, watch } = useForm();
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const { data: optAcceptedInfo } = useQuery({
		queryKey: [GET_OPT_ACCEPTED, applicationId, isAccepted, caseId],
		queryFn: () =>
			casesService.getOptOutAccepted(caseId as string, applicationId),
		enabled: !!applicationId && !!isAccepted && !!caseId,
	});

	React.useEffect(() => {
		if (optAcceptedInfo) {
			setValue('name', optAcceptedInfo.data.name);
			setValue('signature', optAcceptedInfo.data.signature);
			setValue('date', optAcceptedInfo.data.date);
		}
	}, [optAcceptedInfo]);

	React.useEffect(() => {
		if (watch('signature')) {
			const date = new Date();
			const formattedDate = format(date, 'MM/dd/yyyy');
			setValue('date', formattedDate);
		} else {
			setValue('date', '');
		}
	}, [watch('signature')]);

	React.useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	const deleteQuery = (): void => {
		delete query['type'];
		router.replace({
			pathname,
			query,
		});
	};

	const getPayload = (status: 'declined' | 'accepted') => {
		const formData = getValues();
		const data: OptAcceptPayload = {
			name: formData.name,
			signature: formData.signature,
			status: status,
		};
		return data;
	};

	const acceptOpt = async (): Promise<void> => {
		setIsSubmitting(true);
		try {
			const data = getPayload('accepted');
			await casesService.acceptOptOut(applicationId, data);
			await actionRequiredService.putApprovalAction(actionReqId, true);
			toastSuccess('You have successfully accepted your Opt Out Request.');
			refetch && refetch();
			handleCloseModal();
			deleteQuery();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsSubmitting(false);
		}
	};

	const declineOpt = async (): Promise<void> => {
		setIsSubmitting(true);
		try {
			const data = getPayload('declined');
			await casesService.acceptOptOut(applicationId, data);
			toastSuccess('You have successfully declined the Opt Out Request.');
			refetch && refetch();
			handleCloseModal();
			deleteQuery();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
	};

	return (
		<Modal
			classNameCardContainer="max-w-[532px] !px-10"
			withXButton
			title={<ModalHeader title="Accept Opt Out Request" />}
			onClose={handleCloseModal}
		>
			<div className="mt-2">
				<div className="flex md:px-[6.5px] flex-col gap-8">
					<Typography type="regular-16" className="text-center">
						Another case or settlement may currently affect your rights. To
						continue your claim, please review the Opt-Out Request at the link
						below.
					</Typography>
					<Link href={url} target="_blank">
						<Typography
							type="regular-16"
							className="text-center text-blue-link"
						>
							Click here to review and download agreement.
						</Typography>
					</Link>
				</div>
				<Typography type="semibold-18" className="uppercase text-left mt-14">
					Accepted And Agreed To:
				</Typography>
				<form className="mt-4 space-y-4">
					<InputText
						name="name"
						title="Name"
						register={register}
						placeholder={isAccepted ? '' : 'First name and Last name'}
						classNameInput="!bg-gray-5 placeholder:text-[#6E6A73] disabled:!text-black"
						disabled={isAccepted}
					/>
					<InputText
						name="signature"
						title="Signature"
						register={register}
						placeholder={isAccepted ? '' : 'Alex Cullen'}
						classNameInput="!bg-gray-5 placeholder:text-[#0D061614] !font-praise !text-2xl disabled:!text-black"
						disabled={isAccepted}
					/>
					<InputText
						name="date"
						title="Date"
						register={register}
						placeholder="mm/dd/yyyy"
						classNameInput="!bg-gray-5 placeholder:text-[#6E6A73] disabled:!text-black "
						disabled
					/>
					<div className="flex gap-4 justify-center mt-8">
						{!isAccepted && (
							<Button
								size="medium"
								decoration="danger-outline"
								onClick={declineOpt}
								className="!py-4"
								disabled={isSubmitting}
							>
								Decline
							</Button>
						)}
						<Button
							size="medium"
							decoration="primary"
							onClick={acceptOpt}
							className="!py-4"
							disabled={isAccepted || isSubmitting}
							icon={Icons.checkWhite}
							iconRight={!!isAccepted}
							label={isAccepted ? 'Accepted' : 'Accept'}
							classNameButton="!size-4"
						/>
					</div>
				</form>
			</div>
		</Modal>
	);
};
